export class QuotaInformation {
  constructor (quotas) {
    this.contracts = quotas.contracts.map(c => ({
      contractId: c.contractId,
      accountId: c.accountId,
      accountName: c.accountName,
      destinationAccountId: c.destinationAccountId,
      destinationAccountName: c.destinationAccountName,
      status: c.status,
      statusReason: c.statusReason,
      paused: c.paused,
      tract: c.tract,
      logger: c.logger,
      setting: c.setting,
      forester: c.forester,
      quotaContractCount: c.quotaContractCount,
      effectiveDate: c.effectiveDate,
      expirationDate: c.expirationDate
    }))
    this.quotas = quotas.quotas
    this.startOfQuotaPeriod = quotas.startOfQuotaPeriod
    this.startOfNextQuotaPeriod = quotas.startOfNextQuotaPeriod
    this.endOfNextQuotaPeriod = quotas.endOfNextQuotaPeriod
  }

  groupBy = (array, func) => array.reduce((acc, item, index) => {
    const key = func(item, index, array)
    acc[key] ||= []
    acc[key].push(item)
    return acc
  }, {})

  sortAccountNames (accountOne, accountTwo) {
    if (accountOne.name < accountTwo.name) return -1
    if (accountOne.name > accountTwo.name) return 1
    else return 0
  }

  aggregateQuotaInformation () {
    // Group contracts by accountId
    const contractsByAccounts = Object.values(this.groupBy(this.contracts, c => c.accountId))

    // Group contracts by destinationAccountId
    const contractsByDestinationAccounts = Object.values(this.groupBy(this.contracts, c => c.destinationAccountId)).sort((a, b) => a[0].destinationAccountId - b[0].destinationAccountId)

    // Prepare contractAccounts array
    const contractAccounts = contractsByAccounts.map(c => {
      const accountId = c[0].accountId // All items in group have the same accountId
      const accountName = c[0].accountName
      const quotasOnAccount = this.quotas.filter(q => q.accountId === accountId)

      // Group contracts by destinationAccountId within each account
      const destinations = Object.values(this.groupBy(c, contract => contract.destinationAccountId)).map(dest => {
        const destinationAccountId = dest[0].destinationAccountId
        const destinationAccountName = dest[0].destinationAccountName

        const contractInfo = dest.map(contract => ({
          contractId: contract.contractId,
          status: contract.status,
          statusReason: contract.statusReason,
          paused: contract.paused,
          tract: contract.tract,
          logger: contract.logger,
          setting: contract.setting,
          forester: contract.forester,
          quotaContractCount: contract.quotaContractCount
        }))

        return {
          accountId: destinationAccountId,
          name: destinationAccountName,
          contractInfo
        }
      })
        .filter(d => quotasOnAccount.some(q => q.destinationAccountId === d.accountId && q.quotaInEffect !== null && q.quotaInEffect !== undefined))
        .sort(this.sortAccountNames)

      const quotaForDestinations = destinations.map(dest => {
        return {
          accountId: dest.accountId,
          quotaOnAccount: quotasOnAccount.find(q => q.destinationAccountId === dest.accountId)?.quotaInEffect,
          loadsCreated: quotasOnAccount.find(q => q.destinationAccountId === dest.accountId)?.quotaCurrentCount
        }
      })

      return {
        name: accountName,
        accountId,
        quotaOnAccount: quotasOnAccount.filter(q => q.quotaInEffect !== null && q.quotaInEffect !== undefined).reduce((total, q) => total + (q.quotaInEffect || 0), 0),
        loadsCreated: quotasOnAccount.filter(q => q.quotaInEffect !== null && q.quotaInEffect !== undefined).reduce((total, q) => total + (q.quotaCurrentCount || 0), 0),
        hasQuota: quotasOnAccount.some(q => q.quotaInEffect !== null && q.quotaInEffect !== undefined),
        destinations,
        quotaForDestinations
      }
    }).sort(this.sortAccountNames)

    // Prepare destinationAccounts array
    const destinationAccounts = contractsByDestinationAccounts.map(dest => {
      const destinationAccountId = dest[0].destinationAccountId // Grouped by destinationAccountId
      const destinationAccountName = dest[0].destinationAccountName
      const quotasForDestination = this.quotas.filter(q => q.destinationAccountId === destinationAccountId)

      const quotaInfoForContractAccounts = Object.values(this.groupBy(dest, contract => contract.accountId)).map(accountContracts => {
        const accountId = accountContracts[0].accountId
        const accountName = accountContracts[0].accountName

        return {
          accountId,
          name: accountName,
          hasContractInEffectThisPeriod: accountContracts.some(c => new Date(c.effectiveDate) < new Date(this.startOfNextQuotaPeriod) && new Date(this.startOfQuotaPeriod) <= new Date(c.expirationDate)) || quotasForDestination.some(q => q.accountId === accountId && q.quotaInEffect !== null && q.quotaInEffect !== undefined),
          hasContractInEffectNextPeriod: accountContracts.some(c => new Date(c.effectiveDate) < new Date(this.endOfNextQuotaPeriod) && new Date(this.startOfNextQuotaPeriod) <= new Date(c.expirationDate)),
          quota: quotasForDestination.find(q => q.accountId === accountId) ?? null
        }
      }).sort(this.sortAccountNames)

      const endOfNextQuotaPeriodAdjusted = new Date(this.endOfNextQuotaPeriod)
      endOfNextQuotaPeriodAdjusted.setSeconds(endOfNextQuotaPeriodAdjusted.getSeconds() - 1)
      return {
        accountId: destinationAccountId,
        name: destinationAccountName,
        hasQuota: quotasForDestination.some(q => q.quotaInEffect !== null && q.quotaInEffect !== undefined),
        startOfNextQuotaPeriod: this.startOfNextQuotaPeriod,
        endOfNextQuotaPeriod: endOfNextQuotaPeriodAdjusted.toDateString(),
        quotaAssignedToDestination: quotasForDestination.filter(q => q.quotaInEffect !== null && q.quotaInEffect !== undefined).reduce((total, q) => total + (q.quotaInEffect || 0), 0),
        loadsCreatedOnDestination: quotasForDestination.filter(q => q.quotaInEffect !== null && q.quotaInEffect !== undefined).reduce((total, q) => total + (q.quotaCurrentCount || 0), 0),
        quotaInfoForContractAccounts
      }
    }).sort(this.sortAccountNames)

    const endOfQuotaPeriodAdjusted = new Date(this.startOfNextQuotaPeriod)
    endOfQuotaPeriodAdjusted.setSeconds(endOfQuotaPeriodAdjusted.getSeconds() - 1)
    return {
      contractAccounts,
      destinationAccounts,
      startOfQuotaPeriod: this.startOfQuotaPeriod,
      endOfQuotaPeriod: endOfQuotaPeriodAdjusted.toDateString()
    }
  }
}
